"use client";

import { useState } from "react";
import Link from "next/link";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { useTranslations } from "@/lib/i18n/I18nProvider";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@eg/ui";

export default function ForgotPasswordPage() {
  const t = useTranslations();
  const forgotPassword = useAuthStore((s) => s.forgotPassword);
  const isLoading = useAuthStore((s) => s.isLoading);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("auth.tooManyRequests"));
        return;
      }
      setError(t("auth.forgotPasswordError"));
    }
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.forgotPasswordTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            {t("auth.forgotPasswordHint")}
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="mb-1 block">{t("auth.email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            {success ? (
              <p className="text-sm text-emerald-700">
                {t("auth.forgotPasswordSuccess")}
              </p>
            ) : null}
            {error ? <p className="text-error text-sm">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t("auth.sendingResetLink") : t("auth.sendResetLink")}
            </Button>
          </form>
          <p className="text-sm text-gray-600">
            <Link href="/login" className="text-brand-700 underline">
              {t("auth.toLogin")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
